/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { memo, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { DateTime } from 'luxon';
import store from '../../../../../../../../store';

import type { IProps } from './phone-number-filters-modal-container';
import Button from '../../../../../../../../shared/design-system/components/atoms/button';
import Icon from '../../../../../../../../shared/design-system/components/atoms/icon';
import ProspectsFilter from '../../../../../../../../shared/design-system/components/molecules/prospects-filter';
import { FilterType } from '../../../../../../../../shared/design-system/components/molecules/prospects-filter/type';
import { getLabelForCountryCode } from '../../../../../../helpers/helper';
import { RegionOptionForFilters } from '../../../../../../dialer-slice';

export type PhoneNumberFilters = {
  countryCodes: string[];
  regions: RegionOptionForFilters[];
  teamMemberIds: number[];
  purchaseDate: Date[] | null;
};

const PhoneNumberFiltersModal: React.FC<IProps> = ({
  show,
  onClose,
  countriesOptionsForNumbersSearch,
  teamMembersList,
  phoneNumberFilters,
  onFiltersChange,
  regionOptionsListForFilters,
}) => {
  const [filters, setFilters] = useState<PhoneNumberFilters>(
    phoneNumberFilters,
  );
  // Local state to track partial date selection for UI purposes
  const [localDateSelection, setLocalDateSelection] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: phoneNumberFilters.purchaseDate?.[0] || null,
    end: phoneNumberFilters.purchaseDate?.[1] || null,
  });

  useEffect(() => {
    setFilters(phoneNumberFilters);
    setLocalDateSelection({
      start: phoneNumberFilters.purchaseDate?.[0] || null,
      end: phoneNumberFilters.purchaseDate?.[1] || null,
    });
  }, [phoneNumberFilters]);

  // Country codes options
  const countryCodeOptions = useMemo(
    () =>
      countriesOptionsForNumbersSearch?.map((country) => ({
        label: getLabelForCountryCode(country),
        value: country,
      })),
    [countriesOptionsForNumbersSearch],
  );

  // Region options
  const regionOptions = useMemo(
    () =>
      regionOptionsListForFilters?.map((region) => ({
        label: `${region.locality ?? ''} ${region.region ?? ''}, ${
          region.isoCountry ?? ''
        }`,
        value: region,
      })) || [],
    [regionOptionsListForFilters],
  );

  // Team members options
  const mappedTeamMembers = useMemo(
    () =>
      teamMembersList?.length
        ? teamMembersList.map((item) => ({
            ...item,
            firstName: item.name || item.email,
            value: item.id,
          }))
        : [],
    [teamMembersList],
  );

  const setCountryCodeFilters = (selected: any[]) => {
    setFilters((prev) => ({
      ...prev,
      countryCodes: selected.map((item) => item.value || item),
    }));
  };

  const setRegionFilters = (selected: any[]) => {
    setFilters((prev) => ({
      ...prev,
      regions: selected.map((item) => item.value || item),
    }));
  };

  const setTeamMemberFilters = (selected: any[]) => {
    setFilters((prev) => ({
      ...prev,
      teamMemberIds: selected.map((item) => item.id || item),
    }));
  };

  const purchaseDateValues = useMemo(() => {
    const { start, end } = localDateSelection;
    if (!start && !end) return [];
    return [
      { date: start ? DateTime.fromJSDate(start).toISO() ?? '' : '' },
      { date: end ? DateTime.fromJSDate(end).toISO() ?? '' : '' },
    ];
  }, [localDateSelection]);

  const setPurchaseDateFilters = (payload: any) => {
    if (!payload || (Array.isArray(payload) && payload.length === 0)) {
      setLocalDateSelection({ start: null, end: null });
      setFilters((prev) => ({
        ...prev,
        purchaseDate: null,
      }));
      return;
    }

    const dates = Array.isArray(payload) ? payload : [payload];
    const startDate = dates[0] || null;
    const endDate = dates[1] || null;

    // Only process and store when both dates are selected (force range selection)
    const fetch = !!(startDate && endDate);

    if (fetch) {
      const state = store.getState();
      const { timeZone } = state?.myProfile?.myProfile || {};

      // Convert to DateTime with timezone
      const startDateTime = timeZone
        ? DateTime.fromJSDate(startDate).setZone(timeZone)
        : DateTime.fromJSDate(startDate);
      const endDateTime = timeZone
        ? DateTime.fromJSDate(endDate).setZone(timeZone)
        : DateTime.fromJSDate(endDate);

      // Set start date to start of day (00:00:00.000) and end date to end of day (23:59:59.999)
      const processedStartDate = startDateTime.set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
      const processedEndDate = endDateTime.set({
        hour: 23,
        minute: 59,
        second: 59,
        millisecond: 999,
      });

      // Update local state for UI
      setLocalDateSelection({
        start: processedStartDate.toJSDate(),
        end: processedEndDate.toJSDate(),
      });

      // Store DateTime objects for proper formatting later
      setFilters((prev) => ({
        ...prev,
        purchaseDate: [
          processedStartDate.toJSDate(),
          processedEndDate.toJSDate(),
        ],
      }));
    } else if (!startDate && !endDate) {
      // No dates selected
      setLocalDateSelection({
        start: null,
        end: null,
      });
      setFilters((prev) => ({
        ...prev,
        purchaseDate: null,
      }));
    } else if (startDate && !endDate) {
      // Only start date selected - update UI but don't store in filters (force range selection)
      setLocalDateSelection({
        start: startDate,
        end: null,
      });
      // Don't update filter state - user must select both dates
    }
  };

  const handleReset = () => {
    const resetFilters: PhoneNumberFilters = {
      countryCodes: [],
      regions: [],
      teamMemberIds: [],
      purchaseDate: null,
    };
    setFilters(resetFilters);
    setLocalDateSelection({ start: null, end: null });
    onFiltersChange(resetFilters);
    onClose();
  };

  const handleCancel = () => {
    setFilters(phoneNumberFilters);
    setLocalDateSelection({
      start: phoneNumberFilters.purchaseDate?.[0] || null,
      end: phoneNumberFilters.purchaseDate?.[1] || null,
    });
    onClose();
  };

  const handleDone = () => {
    // Only allow applying if purchase date range is complete (both dates selected) or not set
    if (localDateSelection.start && !localDateSelection.end) {
      // Don't apply if only start date is selected - force range selection
      return;
    }
    onFiltersChange(filters);
    onClose();
  };

  // Check if Apply button should be disabled (when only start date is selected)
  const isApplyDisabled = localDateSelection.start && !localDateSelection.end;

  return (
    <Modal
      show={show}
      aria-labelledby="contained-modal-title-vcenter"
      className="prospects-filters-modal phone-number-filters-modal"
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title>
          <span className="semibold-3">Filters</span>
        </Modal.Title>
        <Button className="close" onClick={onClose}>
          <Icon identifier="close" />
        </Button>
      </Modal.Header>
      <Modal.Body>
        <ProspectsFilter
          label="Country Code"
          containerClassName="bs-mb-20"
          onSelect={setCountryCodeFilters}
          options={countryCodeOptions}
          labelKey="label"
          placeholder="Search and Select Country Code"
          values={filters.countryCodes.map(
            (code) =>
              countryCodeOptions?.find((option) => option.value === code) || {
                value: code,
                label: code,
              },
          )}
          uniqueKey="value"
        />

        <ProspectsFilter
          label="Region"
          containerClassName="bs-mb-20"
          onSelect={setRegionFilters}
          options={regionOptions}
          labelKey="label"
          placeholder="Search and Select Region"
          values={filters.regions.map(
            (reg) =>
              regionOptions?.find((option) => {
                if (
                  typeof reg === 'object' &&
                  reg !== null &&
                  typeof option.value === 'object'
                ) {
                  return (
                    option.value.locality === reg.locality &&
                    option.value.region === reg.region &&
                    option.value.isoCountry === reg.isoCountry
                  );
                }
                return option.value === reg;
              }) || {
                value: reg,
                label:
                  typeof reg === 'object'
                    ? `${reg.locality} ${reg.region}, ${reg.isoCountry}`
                    : String(reg),
              },
          )}
          uniqueKey="value"
        />

        <ProspectsFilter
          label="Assigned To"
          containerClassName="bs-mb-20"
          onSelect={setTeamMemberFilters}
          options={mappedTeamMembers}
          labelKey="firstName"
          placeholder="Search and select members"
          values={filters.teamMemberIds.map(
            (id) =>
              mappedTeamMembers?.find((member) => member.id === id) || {
                id,
                firstName: '',
              },
          )}
        />

        <ProspectsFilter
          label="Purchase Date"
          containerClassName="bs-mb-20"
          onSelect={setPurchaseDateFilters}
          options={[]}
          placeholder="Select Date Range"
          values={purchaseDateValues}
          filterType={FilterType.Date}
          isDisabled={false}
        />
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex flex-row align-self-start flex-grow-1">
          <Button
            variant={Button.Variant.Light}
            theme={Button.Theme.Ghost}
            onClick={handleReset}
            className="rst-btn"
          >
            Clear All
          </Button>
          <div className="d-flex flex-row justify-content-end flex-grow-1 gap-16">
            <Button
              variant={Button.Variant.Outlined}
              theme={Button.Theme.Ghost}
              onClick={handleCancel}
              className="phone-number-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant={Button.Variant.Primary}
              onClick={handleDone}
              className="submit-btn"
              disabled={isApplyDisabled}
            >
              Apply
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default memo(PhoneNumberFiltersModal);
